/**
 * Идущее задание (E4.6c): какое задание открыто, приостановлено ли оно, свёрнута ли
 * панель и показана ли подсказка. Живёт в памяти вкладки; отметки целей -- в прогрессе.
 * Смена пользователя закрывает задание.
 */

import { create } from 'zustand';
import { sessionIdentity, useSessionStore } from '@/stores/session';
import type { ExerciseId } from './catalog';

export type ExerciseFeedback = 'not_done' | 'verification_failed' | null;

interface ExerciseStore {
	active: ExerciseId | null;
	paused: ExerciseId | null;
	collapsed: boolean;
	hint: boolean;
	feedback: ExerciseFeedback;
}

const CLOSED: ExerciseStore = {
	active: null,
	paused: null,
	collapsed: false,
	hint: false,
	feedback: null,
};

export const useExerciseStore = create<ExerciseStore>(() => CLOSED);

export function openExercise(id: ExerciseId): void {
	useExerciseStore.setState({
		active: id,
		paused: null,
		collapsed: false,
		hint: false,
		feedback: null,
	});
}

/** Отложить задание: незавершённое можно продолжить из меню «Обучение» */
export function pauseExercise(completed: boolean): void {
	useExerciseStore.setState((state) => ({ ...CLOSED, paused: completed ? null : state.active }));
}

/**
 * Задание, которое можно продолжить: отложенное, а в рабочем контуре -- и открытое (его
 * панели там нет, пользователь вышел из учебного примера, не закончив задание)
 */
export function exerciseResumable(
	state: Pick<ExerciseStore, 'active' | 'paused'>,
	live: boolean,
): ExerciseId | null {
	return state.paused ?? (live ? state.active : null);
}

export function toggleExerciseHint(): void {
	useExerciseStore.setState((state) => ({ hint: !state.hint }));
}

export function toggleExerciseCollapsed(): void {
	useExerciseStore.setState((state) => ({ collapsed: !state.collapsed }));
}

export function setExerciseFeedback(feedback: ExerciseFeedback): void {
	useExerciseStore.setState({ feedback, ...(feedback === null ? { hint: false } : {}) });
}

/** Задание прежнего пользователя не продолжается у следующего. Возвращает отписку */
export function bindExerciseToSessionIdentity(): () => void {
	let identity = sessionIdentity(useSessionStore.getState().user);
	return useSessionStore.subscribe((state) => {
		const next = sessionIdentity(state.user);
		if (next === identity) return;
		identity = next;
		useExerciseStore.setState(CLOSED);
	});
}
